import type { MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { useRecords, useReVerify } from '../api/hooks.js';
import { Card } from '../components/Card.js';
import { cx, LINK, MONO, TABLE_HEAD } from '../components/classes.js';
import { EmptyState, ProblemNote, SkeletonRows } from '../components/Notes.js';
import { Main, PageHeader } from '../components/PageHeader.js';
import { StatusGlyph } from '../components/StatusGlyph.js';
import { formatInstant, PURPOSE_LABELS, resultHeadline } from '../lib/format.js';
import type { Verification } from '../lib/types.js';
import { canIssue, useSessionStore } from '../store/session.js';
import { recordStatus, recordTitle } from './Home.js';

const GRID =
  'grid grid-cols-[22px_minmax(0,1fr)_200px_150px_200px_80px] items-center gap-5 px-6';

/** The organisation's records, newest first (the "Records" nav word). */
export function Records() {
  const navigate = useNavigate();
  const records = useRecords();
  const reverify = useReVerify();
  const role = useSessionStore((s) => s.role);
  const mayIssue = canIssue(role);
  const rows = records.data?.records ?? [];
  // A fresh record for the row's subject (never an idempotent replay); the
  // row itself stays a link to the existing record.
  const reVerifyRow = (e: MouseEvent, r: Verification) => {
    e.preventDefault();
    e.stopPropagation();
    if (!r.entity || reverify.isPending) return;
    reverify.mutate(
      { entity_id: r.entity.id, purpose: r.purpose, reference: r.reference },
      {
        onSuccess: (env) =>
          navigate(`/records/${env.data.id}`, { state: { issued: true } }),
      },
    );
  };
  return (
    <Main>
      <PageHeader
        title="Records"
        line={records.data ? `${rows.length} issued · newest first` : ' '}
      />
      {records.isError ? <ProblemNote error={records.error} /> : null}
      {reverify.isError ? <ProblemNote error={reverify.error} /> : null}
      <Card>
        <div className={cx(GRID, 'py-3', TABLE_HEAD)}>
          <span />
          <span>SUBJECT</span>
          <span>RESULT</span>
          <span>PURPOSE</span>
          <span className="text-right">ISSUED</span>
          <span />
        </div>
        {records.isPending ? (
          <SkeletonRows rows={4} />
        ) : rows.length === 0 ? (
          <div className="border-t border-line">
            <EmptyState title="No records yet">
              Issue a verification from a profile, or record a “no match” from a results
              list.
            </EmptyState>
          </div>
        ) : (
          rows.map((r) => (
            <Link
              key={r.id}
              to={`/records/${r.id}`}
              className={cx(
                GRID,
                'border-t border-line py-4 text-[14px] hover:bg-sunk/60',
              )}
            >
              <StatusGlyph status={recordStatus(r)} />
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="truncate text-[15px] font-semibold">
                  {recordTitle(r)}
                </span>
                <span className={cx(MONO, 'text-[12px] text-mute')}>{r.id}</span>
              </span>
              <span className="text-mute">
                {r.entity ? resultHeadline(r.result) : 'No match'}
              </span>
              <span className="text-mute">
                {r.purpose ? PURPOSE_LABELS[r.purpose] : '—'}
              </span>
              <span className={cx(MONO, 'text-right text-[12px] text-mute')}>
                {formatInstant(r.issued_at)}
              </span>
              {r.entity && mayIssue ? (
                <button
                  type="button"
                  onClick={(e) => reVerifyRow(e, r)}
                  disabled={reverify.isPending}
                  className={cx(
                    LINK,
                    'cursor-pointer justify-self-end text-[13px] disabled:cursor-default disabled:opacity-60',
                  )}
                >
                  Re-verify
                </button>
              ) : (
                <span />
              )}
            </Link>
          ))
        )}
      </Card>
    </Main>
  );
}
